import React, {forwardRef} from 'react';

type CSSInput = TemplateStringsArray | string | Record<string, unknown>;
type StyledTag = keyof JSX.IntrinsicElements | React.ComponentType<any>;

const injectedRules = new Set<string>();

const hash = (value: string) => {
  let acc = 0;
  for (let index = 0; index < value.length; index += 1) {
    acc = (acc * 33 + value.charCodeAt(index)) >>> 0;
  }
  return acc.toString(36);
};

const toKebab = (value: string) => value.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);

const normalizeCss = (input: CSSInput, values: unknown[] = []) => {
  if (typeof input === 'string') {
    return input;
  }

  if (Array.isArray(input)) {
    let output = '';
    input.forEach((part, index) => {
      output += part;
      if (index < values.length) {
        output += String(values[index] ?? '');
      }
    });
    return output;
  }

  return Object.entries(input)
    .filter(([, value]) => value !== undefined && value !== null && value !== false)
    .map(([key, value]) => `${toKebab(key)}:${String(value)};`)
    .join('');
};

const injectRule = (rule: string) => {
  if (typeof document === 'undefined' || injectedRules.has(rule)) {
    return;
  }

  const style = document.createElement('style');
  style.setAttribute('data-goober-shim', 'true');
  style.appendChild(document.createTextNode(rule));
  document.head.appendChild(style);
  injectedRules.add(rule);
};

export const css = (input: CSSInput, ...values: unknown[]) => {
  const body = normalizeCss(input, values);
  const className = `goober-${hash(body)}`;
  injectRule(`.${className}{${body}}`);
  return className;
};

export const glob = (input: CSSInput, ...values: unknown[]) => {
  injectRule(normalizeCss(input, values));
};

export const keyframes = (input: CSSInput, ...values: unknown[]) => {
  const body = normalizeCss(input, values);
  const name = `goober-kf-${hash(body)}`;
  injectRule(`@keyframes ${name}{${body}}`);
  return name;
};

export const setup = () => {};

type StyledFactory = ((input: CSSInput, ...values: unknown[]) => React.ForwardRefExoticComponent<any>) & {
  [K in keyof JSX.IntrinsicElements]: (input: CSSInput, ...values: unknown[]) => React.ForwardRefExoticComponent<any>;
};

const createStyled = (tag: StyledTag) => (input: CSSInput, ...values: unknown[]) => {
  const className = css(input, ...values);

  return forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(function GooberStyled(props, ref) {
    const {className: existingClassName, ...rest} = props;
    const mergedClassName = [className, existingClassName].filter(Boolean).join(' ');
    return React.createElement(tag as React.ElementType, {
      ...rest,
      ref,
      className: mergedClassName,
    });
  });
};

export const styled = new Proxy(createStyled as unknown as StyledFactory, {
  apply(_target, _thisArg, args: [StyledTag]) {
    return createStyled(args[0]);
  },
  get(_target, property: string) {
    return createStyled(property as StyledTag);
  },
});

export const extractCss = () => '';

export default styled;
